'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { apiClient } from '@/lib/api-client';
import { clearAuthStorage, getAccessToken, hasStoredUser, isAuthSessionEnded } from '@/lib/auth-storage';
import { decodeJWT } from '@/lib/jwt-utils';
import { isAuthExemptPublicRoute } from '@/lib/i18n/public-serbian-routes';
const IDLE_MINUTES = Number(process.env.NEXT_PUBLIC_SESSION_IDLE_MINUTES ?? '480');
const IDLE_MS = Math.max(1, IDLE_MINUTES) * 60 * 1000;
const REFRESH_BEFORE_EXPIRY_MS = 90_000;
const ACTIVITY_THROTTLE_MS = 5_000;
const TICK_MS = 30_000;
let lastActivityAt = typeof Date.now === 'function' ? Date.now() : 0;
function touchActivity() {
  lastActivityAt = Date.now();
}
function isIdlePastLimit(): boolean {
  return Date.now() - lastActivityAt > IDLE_MS;
}

/**
 * Activity-aware session keeper: proactive JWT refresh while the user is active,
 * and local logout (with server revoke) after idle timeout.
 */
export function SessionKeeper() {
  const $ = _c(3);
  const pathname = usePathname();
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      if (isAuthExemptPublicRoute(pathname)) {
        return;
      }
      touchActivity();
      const tick = _temp;
      const onActivity = _temp2;
      const onVisibility = () => {
        touchActivity();
        if (document.visibilityState === "visible") {
          tick();
        }
      };
      window.addEventListener("pointerdown", onActivity, {
        passive: true
      });
      window.addEventListener("keydown", onActivity, {
        passive: true
      });
      document.addEventListener("visibilitychange", onVisibility, {
        passive: true
      });
      const intervalId = window.setInterval(() => {
        tick();
      }, TICK_MS);
      tick();
      return () => {
        window.removeEventListener("pointerdown", onActivity);
        window.removeEventListener("keydown", onActivity);
        document.removeEventListener("visibilitychange", onVisibility);
        window.clearInterval(intervalId);
      };
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}
function _temp2() {
  const now = Date.now();
  if (now - lastActivityAt < ACTIVITY_THROTTLE_MS) {
    return;
  }
  touchActivity();
}
async function _temp() {
  if (isAuthSessionEnded() && !hasStoredUser()) {
    return;
  }
  if (document.visibilityState === "hidden") {
    return;
  }
  if (isIdlePastLimit()) {
    await apiClient.logout();
    if (!isAuthExemptPublicRoute(window.location.pathname)) {
      window.location.href = "/login";
    }
    return;
  }
  let token = getAccessToken();
  if (!token && hasStoredUser()) {
    try {
      token = await apiClient.refreshToken();
    } catch {
      return;
    }
  }
  if (!token) {
    return;
  }
  const decoded = decodeJWT(token);
  if (!decoded?.exp) {
    return;
  }
  const expiresAtMs = decoded.exp * 1000;
  const msLeft = expiresAtMs - Date.now();
  if (msLeft > REFRESH_BEFORE_EXPIRY_MS) {
    return;
  }
  try {
    await apiClient.refreshToken();
  } catch {}
}